import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Paper, Typography, Button, Chip } from "@mui/material";
import PageHeader from "../../components/common/PageHeader";
import ProviderForm from "../../components/forms/ProviderForm";
import AlertMessage from "../../components/common/AlertMessage";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import { useAlert } from "../../context/AlertContext";
import api from "../../services/api";
import { Provider } from "../../types";
import { formatDateTime } from "../../utils/formatters";

const ProviderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const alert = useAlert();
  const isNew = id === "new";

  // State variables
  const [provider, setProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState<boolean>(!isNew);
  const [error, setError] = useState<string | null>(null);

  // Fetch provider if editing
  useEffect(() => {
    if (isNew) return;

    const fetchProvider = async () => {
      try {
        setLoading(true);
        const response = await api.providers.getById(id as string);
        setProvider(response.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching provider:", error);
        setError("Failed to load provider. Please try again.");
        alert.error("Failed to fetch provider");
      } finally {
        setLoading(false);
      }
    };

    fetchProvider();
  }, [id, isNew, alert]);

  // Get provider color
  const getProviderColor = (
    provider: string,
  ):
    | "primary"
    | "secondary"
    | "success"
    | "info"
    | "warning"
    | "error"
    | "default" => {
    if (provider.startsWith("openai")) return "primary";
    if (provider.startsWith("anthropic")) return "info";
    if (provider.startsWith("google")) return "success";
    if (provider.startsWith("azure")) return "secondary";
    if (provider.startsWith("mistral")) return "warning";
    if (provider.startsWith("cohere")) return "error";
    return "default";
  };

  // Get provider display name
  const getProviderDisplayName = (provider: string): string => {
    // Extract the base provider and model name
    const parts = provider.split(":");
    if (parts.length === 1) {
      return provider.charAt(0).toUpperCase() + provider.slice(1);
    }

    const baseName = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const model = parts.slice(1).join(":");

    return `${baseName} - ${model}`;
  };

  return (
    <Box>
      <PageHeader
        title={isNew ? "Create Provider" : "Edit Provider"}
        subtitle={
          isNew
            ? "Create a new LLM provider for your evaluations"
            : "Edit an existing LLM provider"
        }
        action={
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => navigate("/providers")}
          >
            Back to Providers
          </Button>
        }
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Providers", path: "/providers" },
          { label: isNew ? "Create" : "Edit", path: "" },
        ]}
      />

      <LoadingBackdrop open={loading && !provider && !isNew} />

      {error && (
        <AlertMessage
          open={Boolean(error)}
          onClose={() => setError(null)}
          severity="error"
          title="Error"
          message={error}
        />
      )}

      {!isNew && provider && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <Typography variant="h5" fontWeight="medium">
                  {provider.name}
                </Typography>
                <Chip
                  label={getProviderDisplayName(provider.provider)}
                  color={getProviderColor(provider.provider)}
                  variant="outlined"
                  size="small"
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Configuration:{" "}
                {provider.config && Object.keys(provider.config).length > 0
                  ? `Custom (${Object.keys(provider.config).length} setting(s))`
                  : "Default configuration"}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Last updated: {formatDateTime(provider.updatedAt)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}

      {(isNew || provider) && (
        <ProviderForm initialData={provider || undefined} isEdit={!isNew} />
      )}
    </Box>
  );
};

export default ProviderDetail;
